import React from 'react';
import type { Badge as BadgeType } from '../../services/pointsService';

interface BadgeProps {
  badge: BadgeType;
  unlocked?: boolean;
  progress?: number;
  size?: 'sm' | 'md' | 'lg';
  showProgress?: boolean;
  onClick?: () => void;
}

const Badge: React.FC<BadgeProps> = ({
  badge,
  unlocked = false,
  progress = 0,
  size = 'md',
  showProgress = false,
  onClick
}) => {
  // Tamanhos
  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const iconSizes = {
    sm: 'text-2xl',
    md: 'text-4xl',
    lg: 'text-5xl'
  };

  // Cores por tier
  const tierColors = {
    bronze: 'from-amber-700 to-amber-900',
    silver: 'from-gray-300 to-gray-500',
    gold: 'from-yellow-400 to-yellow-600',
    diamond: 'from-cyan-300 to-blue-500',
    platinum: 'from-gray-200 to-gray-400'
  };

  const tierBorders = {
    bronze: 'border-amber-800',
    silver: 'border-gray-400',
    gold: 'border-yellow-500',
    diamond: 'border-blue-400',
    platinum: 'border-gray-300'
  };

  return (
    <div 
      className={`relative ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Badge Container */}
      <div
        className={`
          ${sizes[size]}
          rounded-full
          flex items-center justify-center
          border-4 ${tierBorders[badge.tier]}
          bg-gradient-to-br ${tierColors[badge.tier]}
          shadow-lg
          transition-all duration-300
          ${unlocked ? 'opacity-100 scale-100' : 'opacity-40 grayscale scale-95'}
          ${onClick ? 'hover:scale-110 hover:shadow-xl' : ''}
        `}
        style={{
          backgroundColor: badge.color || undefined
        }}
      >
        {/* Ícone ou Imagem */}
        {badge.icon && badge.icon.startsWith('http') ? (
          <img 
            src={badge.icon} 
            alt={badge.name}
            className={`w-full h-full object-cover rounded-full ${unlocked ? '' : 'opacity-50'}`}
          />
        ) : (
          <span className={`${iconSizes[size]} ${unlocked ? '' : 'opacity-50'}`}>
            {badge.icon || '🏆'}
          </span>
        )}

        {/* Badge Bloqueado - Cadeado */}
        {!unlocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-full">
            <i className="ri-lock-fill text-white text-2xl"></i>
          </div>
        )}
      </div>

      {/* Barra de Progresso */}
      {showProgress && !unlocked && progress > 0 && (
        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-full px-2">
          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-center text-gray-600 mt-1">{progress}%</p>
        </div>
      )}

      {/* Nome do Badge (tooltip ou abaixo) */}
      <div className="mt-2 text-center">
        <p className={`font-semibold ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'} ${unlocked ? 'text-gray-800' : 'text-gray-400'}`}>
          {badge.name}
        </p>
        {badge.description && (
          <p className={`${size === 'sm' ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>
            {badge.description}
          </p>
        )}
      </div>

      {/* Pontos de Recompensa */}
      {unlocked && badge.pointsReward > 0 && (
        <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full px-2 py-1 text-xs font-bold shadow-md">
          +{badge.pointsReward}
        </div>
      )}
    </div>
  );
};

export default Badge;
